import { useState, useContext, useEffect, useRef } from "react";
import { CombinedContext } from "../../context/combinedContext";
import { Edit, Camera, Phone, Share2, MapPin, ChevronDown } from "lucide-react";
import "./profileStaffCard.css";
import StaffServicesModal from "./staffServicesModal/staffServicesModal";
import StaffInfoModal from "./staffInfoModal/staffInfoModal";
import companyBasedRequest from "../../request/companyBasedRequest";
import translations from "../../translations/translations";
import { useLocation, useNavigate } from "react-router-dom";
import ProfileUploadModal from "../teamPage/profilePhotoModal/profilePhotoModal";
import ProfileStaffSkeleton from "./profileStaffSkeleton/profileStaffSkeleton";
import ShopMapPreview from "../../utils/shopMap/shopMapPreview";

/* ── helpers ── */
function formatPhone(number) {
	if (!number) return "";
	const digits = number.replace(/\D/g, "");
	if (digits.length === 11 && digits.startsWith("1")) {
		return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
	}
	return number.startsWith("+") ? number : `+${number}`;
}

function formatTime24To12(time24) {
	if (!time24) return "";
	const [h, m] = time24.split(":").map(Number);
	const hour12 = h % 12 || 12;
	const minutes = m.toString().padStart(2, "0");
	return `${hour12}:${minutes} ${h >= 12 ? "PM" : "AM"}`;
}

function formatUrl(url) {
	if (!url) return "";
	return url.startsWith("http://") || url.startsWith("https://") ? url : `https://${url}`;
}

/* ── Sidebar content — rendered in mobile panel AND desktop sidebar ── */
function SidebarContent({ shopData, owner, language, t, navigate }) {
	return (
		<div className="sf-profile-sidebar">
			<div className="sf-profile-sidebar-section">
				<ShopMapPreview lat={shopData.lat} lng={shopData.lng} />
			</div>

			{shopData.description && (
				<div className="sf-profile-sidebar-section">
					<div className="sf-shop-sidebar-title-row">
						<h3 className="sf-shop-sidebar-title">{t[language].shopPage.sidebar.description}</h3>
					</div>
					<p className="sf-shop-description">{shopData.description}</p>
				</div>
			)}

			<div className="sf-shop-sidebar-section">
				<div className="sf-shop-sidebar-title-row">
					<h3 className="sf-shop-sidebar-title">{t[language].shopPage.sidebar.staff}</h3>
					{/* <button className="sf-edit-btn" onClick={() => navigate("/team")}>
                        <Edit size={14} />
                    </button> */}
				</div>
				<div className="sf-profile-staff-list">
					{shopData.staffs
						?.filter((s) => s.status === "active")
						.map((b) => (
							<div className="sf-profile-staff-item" key={b.userId || b.email}>
								{b.photo ? (
									<img className="sf-profile-staff-avatar" src={b.photo} alt={b.firstName} />
								) : (
									<div className="sf-profile-staff-avatar-fallback">
										<span>{b.firstName?.charAt(0).toUpperCase()}</span>
									</div>
								)}
								<div className="sf-profile-staff-name">{b.firstName}</div>
							</div>
						))}
				</div>
			</div>

			<div className="sf-profile-sidebar-section">
				<div className="sf-shop-sidebar-title-row">
					<h3 className="sf-profile-sidebar-title">{t[language].profile.sidebar.contact.title}</h3>
				</div>
				<hr className="sf-shop-divider" />

				{owner && (
					<>
						<div className="sfc-owner-row">
							{owner.photo ? (
								<img src={owner.photo} alt="" className="sfc-owner-avatar" />
							) : (
								<div className="sfc-owner-avatar-fallback">{owner.firstName?.charAt(0)}</div>
							)}
							<div className="sfc-owner-meta">
								<span className="sfc-owner-label">{language === "es" ? "Propietario" : "Business Owner"}</span>
								<span className="sfc-owner-name">
									{owner.firstName} {owner.lastName}
								</span>
								{owner.phone && (
									<a href={`tel:${owner.phone}`} className="sfc-owner-phone">
										<Phone size={12} strokeWidth={1.8} />
										{formatPhone(owner.phone)}
									</a>
								)}
							</div>
						</div>
						<hr className="sf-shop-divider" />
					</>
				)}
                
				{shopData.openingHours?.length > 0 && (
					<div>
						<div className="sf-shop-sidebar-title-row">
							<h3 className="sf-profile-sidebar-title">{t[language].profile.sidebar.hours.title}</h3>
						</div>
						<div className="sf-shop-hours-full">
							{shopData.openingHours.map((day, i) => {
								const todayIndex = (new Date().getDay() + 6) % 7;
								const isToday = todayIndex === i;
								const dayName = language === "es" ? day.dayES : day.dayEN;
								return (
									<div key={i} className={`sf-shop-hours-row ${isToday ? "is-today" : ""}`}>
										<div className="sf-shop-hours-day">{dayName}</div>
										<div className="sf-shop-hours-time">
											{day.closed
												? t[language].profile.sidebar.hours.closed
												: `${formatTime24To12(day.open)} – ${formatTime24To12(day.close)}`}
										</div>
									</div>
								);
							})}
						</div>
					</div>
				)}

				{shopData.socialLinks && Object.values(shopData.socialLinks).some(Boolean) && (
					<div>
						<hr className="sf-shop-divider" />
						<h3 className="sf-profile-sidebar-title">{t[language].shopPage.sidebar.socialTitle}</h3>
						<div className="sf-shop-socials">
							{shopData.socialLinks.instagram && (
								<div className="sf-social-item">
									<a href={formatUrl(shopData.socialLinks.instagram)} target="_blank" rel="noopener noreferrer" className="sf-social-link">
										<img src="https://cdn-icons-png.flaticon.com/512/1384/1384031.png" alt="Instagram" />
									</a>
									<p>Instagram</p>
								</div>
							)}
							{shopData.socialLinks.facebook && (
								<div className="sf-social-item">
									<a href={formatUrl(shopData.socialLinks.facebook)} target="_blank" rel="noopener noreferrer" className="sf-social-link">
										<img src="https://cdn-icons-png.flaticon.com/512/1384/1384005.png" alt="Facebook" />
									</a>
									<p>Facebook</p>
								</div>
							)}
							{shopData.socialLinks.tiktok && (
								<div className="sf-social-item">
									<a href={formatUrl(shopData.socialLinks.tiktok)} target="_blank" rel="noopener noreferrer" className="sf-social-link">
										<img src="https://cdn-icons-png.flaticon.com/512/3046/3046120.png" alt="TikTok" />
									</a>
									<p>TikTok</p>
								</div>
							)}
							{shopData.socialLinks.website && (
								<div className="sf-social-item">
									<a href={formatUrl(shopData.socialLinks.website)} target="_blank" rel="noopener noreferrer" className="sf-social-link">
										<img src="https://cdn-icons-png.flaticon.com/512/545/545674.png" alt="Website" />
									</a>
									<p>{t[language].shopPage.sidebar.websiteTitle}</p>
								</div>
							)}
						</div>
					</div>
				)}
			</div>
		</div>
	);
}

/* ============================================================
   MAIN COMPONENT
============================================================ */
const ProfileStaffCard = () => {
	const { userData, language } = useContext(CombinedContext);

	const [staff, setStaff] = useState(null);
	const [staffServiceIds, setStaffServiceIds] = useState([]);
	const [loading, setLoading] = useState(true);
	const [activeModal, setActiveModal] = useState(null);
	const [shopServices, setShopServices] = useState([]);
	const [shopData, setShopData] = useState(null);
	const [activeCategory, setActiveCategory] = useState(null);
	const [shareCopied, setShareCopied] = useState(false);
	const [activeTab, setActiveTab] = useState("services");
	const [lastTab, setLastTab] = useState("services");
	const [openCategories, setOpenCategories] = useState({});

	const categoryRefs = useRef({});
	const tabsRef = useRef(null);
	const tabContainerRef = useRef(null);

	const location = useLocation();
	const navigate = useNavigate();

	const TAB_ORDER = ["services", "info"];

	useEffect(() => {
		if (!location.state?.openModal) return;
		setActiveModal(location.state.openModal);
	}, [location.state, navigate]);

	useEffect(() => {
		if (!userData?.user) return;
		setStaff({
			userId: userData.user.userId,
			firstName: userData.user.firstName,
			lastName: userData.user.lastName,
			bio: userData.user.bio || "",
			phone: userData.user.phoneNumber || "",
			photo: userData.user.photo || null,
		});
		setStaffServiceIds(userData.user.services || []);
		setLoading(false);
	}, [userData]);

	useEffect(() => {
		if (!userData?.user?.shopId) return;
		companyBasedRequest
			.getShopServices(userData.user.shopId, userData.cognitoTokens.AccessToken)
			.then((res) => setShopServices(res.services || []))
			.catch(console.error);
	}, [userData]);

	useEffect(() => {
		if (!userData?.user?.shopId) return;
		companyBasedRequest
			.getShopWithstaffsByIdPrivate(userData.user.shopId, userData.cognitoTokens.AccessToken)
			.then((res) => setShopData(res.shop))
			.catch(console.error);
	}, [userData]);

	const displayServices = staffServiceIds.map((id) => shopServices.find((s) => s.serviceId === id)).filter(Boolean);

	const staffServicesByCategory = (() => {
		const cats = shopData?.serviceCategories || [];
		const grouped = cats
			.map((cat) => ({
				category: cat,
				services: displayServices.filter((s) => s.categoryId === cat.categoryId),
			}))
			.filter((g) => g.services.length > 0);

		const knownIds = new Set(cats.map((c) => c.categoryId));
		const orphans = displayServices.filter((s) => !knownIds.has(s.categoryId));
		if (orphans.length) {
			grouped.push({
				category: { categoryId: "uncategorized", name: { en: "Other", es: "Otros" } },
				services: orphans,
			});
		}
		return grouped;
	})();

	useEffect(() => {
		if (!staffServicesByCategory.length) return;
		setOpenCategories(Object.fromEntries(staffServicesByCategory.map(({ category }) => [category.categoryId, true])));
	}, [shopData]);

	const toggleCategory = (id) => setOpenCategories((prev) => ({ ...prev, [id]: !prev[id] }));

	/* scroll-spy */
	useEffect(() => {
		if (!staffServicesByCategory.length || activeTab !== "services") return;
		const firstId = staffServicesByCategory[0].category.categoryId;
		if (!activeCategory) setActiveCategory(firstId);
		const stickyHeight = tabsRef.current?.offsetHeight || 0;
		const observer = new IntersectionObserver(
			(entries) => {
				const visible = entries.filter((e) => e.isIntersecting);
				if (visible.length) {
					const closest = visible.reduce((a, b) => (Math.abs(a.boundingClientRect.top) < Math.abs(b.boundingClientRect.top) ? a : b));
					setActiveCategory(closest.target.getAttribute("data-category-id"));
				} else if (window.scrollY < 200) {
					setActiveCategory(firstId);
				}
			},
			{ rootMargin: `-${stickyHeight + 8}px 0px -70% 0px`, threshold: [0.05, 0.15] },
		);
		Object.values(categoryRefs.current).forEach((el) => el && observer.observe(el));
		return () => observer.disconnect();
	}, [staffServicesByCategory, activeTab]);

	useEffect(() => {
		if (!activeCategory || !tabsRef.current) return;
		const btn = tabsRef.current.querySelector(`[data-category-tab="${activeCategory}"]`);
		if (btn) tabsRef.current.scrollTo({ left: btn.offsetLeft, behavior: "smooth" });
	}, [activeCategory]);

	useEffect(() => {
		const container = tabContainerRef.current;
		if (!container) return;
		const panel = container.querySelector(".sfc-tab-panel.entering") || container.querySelector(".sfc-tab-panel:not(.hidden):not(.hidden-left)");
		if (!panel) return;
		requestAnimationFrame(() => {
			container.style.height = `${panel.scrollHeight}px`;
		});
	}, [activeTab, shopData, staffServicesByCategory]);

	const handleTabChange = (newTab) => {
		if (newTab === activeTab) return;
		setLastTab(activeTab);
		setActiveTab(newTab);
		document.getElementById("root")?.scrollTo({ top: 0, behavior: "smooth" });
	};

	const getSlideDirection = () => {
		const oldIndex = TAB_ORDER.indexOf(lastTab);
		const newIndex = TAB_ORDER.indexOf(activeTab);
		return newIndex > oldIndex ? "right" : "left";
	};

	const handleShare = () => {
		const url = `https://reservasinfila.com/shops/${shopData?.shopId}/staff/${staff?.userId}`;
		if (navigator.share) {
			navigator.share({ url });
		} else {
			navigator.clipboard.writeText(url);
			setShareCopied(true);
			setTimeout(() => setShareCopied(false), 2000);
		}
	};

	if (loading || !staff || !shopData) return <ProfileStaffSkeleton />;

	const slideDir = getSlideDirection();
	const owner = shopData?.staffs?.find((s) => s.role === "owner");
	const reviewCount = shopData.reviews?.length || 0;

	return (
		<div className="sfc-page">
			{/* ── CAROUSEL — full width, outside grid, same as admin card ── */}

			{/* ── TWO-COLUMN GRID ── */}
			<div className="sfc-content">
				{/* ── LEFT / MAIN ── */}
				<div className="sfc-main">
					<StaffCarousel images={shopData.images || []} onShare={handleShare} shareCopied={shareCopied} />

					<div className="sf-shop-profile-mobile-div">
						{/* shop name + address + reviews */}
						<div className="sf-profile-section-name">
							<h1 className="sf-profile-name">{shopData.name}</h1>
						</div>
						<p className="sf-profile-location">
							<MapPin size={16} strokeWidth={1.8} className="sf-location-pin" />
							{shopData.address || <span className="sf-placeholder">{translations[language].profile.addAddress}</span>}
						</p>
						{shopData.addressDescription && <p className="sf-profile-location sf-profile-location--line2">{shopData.addressDescription}</p>}
						{reviewCount > 0 && (
							<p className="sf-profile-reviews">
								<span className="sf-profile-star">★</span>
								{shopData.reviewSummary?.averageRating}
								<span className="sf-profile-reviews-header">
									({reviewCount} {translations[language].shopPage.main.reviews})
								</span>
							</p>
						)}

						{/* staff identity card */}
						<div className="sfc-identity">
							<div className="sfc-avatar-wrap">
								{staff.photo ? (
									<img src={staff.photo} alt="" className="sfc-avatar" />
								) : (
									<div className="sfc-avatar-fallback">{staff.firstName?.charAt(0)}</div>
								)}
								<button className="sfc-avatar-camera" onClick={() => setActiveModal("avatar")}>
									<Camera size={13} />
								</button>
							</div>
							<div className="sfc-identity-text">
								<div className="sfc-identity-name-row">
									<h2 className="sfc-identity-name">
										{staff.firstName} {staff.lastName}
									</h2>
									<button className="sf-edit-btn sf-edit-btn-centered" onClick={() => setActiveModal("info")}>
										<Edit size={14} />
									</button>
								</div>
								{staff.bio && <p className="sfc-identity-bio">{staff.bio}</p>}
								{staff.phone && (
									<p className="sfc-identity-phone">
										<Phone size={13} strokeWidth={1.8} />
										{formatPhone(staff.phone)}
									</p>
								)}
							</div>
						</div>

						{/* ── SERVICES SECTION TITLE ── */}
						<h2 className="sf-profile-section-title">
							{translations[language].shopPage.main.services}
							<button className="sf-edit-btn" onClick={() => setActiveModal("services")}>
								<Edit size={14} />
							</button>
						</h2>

						{/* tabs — Info tab hidden on desktop */}
						<div className="sf-staff-shop-tabs">
							<button className={`sf-shop-tab-btn ${activeTab === "services" ? "active" : ""}`} onClick={() => handleTabChange("services")}>
								{translations[language].shopPage.main.services}
							</button>
							<button
								className={`sf-shop-tab-btn sfc-info-tab-mobile ${activeTab === "info" ? "active" : ""}`}
								onClick={() => handleTabChange("info")}
							>
								{translations[language].shopPage.sidebar.information}
							</button>
						</div>

						{/* tab panels */}
						<div className="sfc-tab-container" ref={tabContainerRef}>
							{/* SERVICES */}
							<div
								className={`sfc-tab-panel ${
									activeTab === "services" ? `entering ${slideDir}` : slideDir === "right" ? "hidden" : "hidden-left"
								}`}
							>
								<div className="sfc-services-wrap">
									{displayServices.length > 0 ? (
										<div className="sf-profile-service-list">
											{staffServicesByCategory.length > 0 && (
												<div ref={tabsRef} className="sf-profile-tabs">
													{staffServicesByCategory.map(({ category }) => (
														<button
															key={category.categoryId}
															data-category-tab={category.categoryId}
															className={`sf-profile-tab-btn ${activeCategory === category.categoryId ? "active" : ""}`}
															onClick={() => {
																setActiveCategory(category.categoryId);
																categoryRefs.current[category.categoryId]?.scrollIntoView({
																	behavior: "smooth",
																	block: "start",
																});
															}}
														>
															{category.name?.[language]}
														</button>
													))}
												</div>
											)}

											{staffServicesByCategory.map(({ category, services }) => {
												const isOpen = openCategories[category.categoryId] !== false;
												return (
													<div
														key={category.categoryId}
														ref={(el) => (categoryRefs.current[category.categoryId] = el)}
														data-category-id={category.categoryId}
														className="sf-service-category-section"
													>
														{staffServicesByCategory.length > 1 && (
															<div
																className="sf-service-category-div"
																onClick={() => toggleCategory(category.categoryId)}
																style={{
																	cursor: "pointer",
																	display: "flex",
																	alignItems: "center",
																	justifyContent: "space-between",
																}}
															>
																<h3 className="sf-service-category-title">{category.name?.[language]}</h3>
																<ChevronDown size={16} className={`sf-category-chevron ${isOpen ? "open" : ""}`} />
															</div>
														)}

														<div
															className="sf-category-body"
															ref={(el) => {
																if (!el) return;
																el.style.maxHeight = isOpen ? `${el.scrollHeight}px` : "0px";
															}}
															style={{ overflow: "hidden", transition: "max-height 500ms ease" }}
														>
															{services.map((s) => (
																<div key={s.serviceId}>
																	<div className="sf-profile-service-item">
																		<div className="sf-profile-service-info">
																			<div className="sf-profile-service-name">
																				{s.name}
																				{s.isHomeService && (
																					<span className="sf-profile-service-home">
																						{" · "}
																						{language === "es" ? "Domicilio" : "Home service"}
																					</span>
																				)}
																			</div>
																			{s.description && (
																				<div className="sf-profile-service-duration">{s.description}</div>
																			)}
																		</div>
																		<div className="sf-profile-service-right">
																			<div className="sf-profile-service-price">
																				{s.currency || "USD"} {s.price ?? 0}
																			</div>
																			<div className="sf-profile-service-duration">{s.duration} min</div>
																		</div>
																	</div>
																	<div className="sf-service-divider-shop" aria-hidden="true" />
																</div>
															))}
														</div>
													</div>
												);
											})}
										</div>
									) : (
										<div className="sf-empty-block" onClick={() => setActiveModal("services")}>
											{language === "es" ? "+ Agregar servicios" : "+ Add services"}
										</div>
									)}
								</div>
							</div>

							{/* INFO — mobile only */}
							<div
								className={`sfc-tab-panel sfc-info-panel-mobile ${
									activeTab === "info" ? `entering ${slideDir}` : slideDir === "right" ? "hidden" : "hidden-left"
								}`}
							>
								<SidebarContent shopData={shopData} owner={owner} language={language} t={translations} navigate={navigate} />
							</div>
						</div>
					</div>
				</div>

				{/* ── RIGHT / DESKTOP SIDEBAR ── */}
				<div className="sfc-sidebar-tablet">
					<SidebarContent shopData={shopData} owner={owner} language={language} t={translations} navigate={navigate} />
				</div>
			</div>

			{/* ── MODALS ── */}
			{activeModal === "info" && <StaffInfoModal open onClose={() => setActiveModal(null)} staff={staff} setStaff={setStaff} />}
			{activeModal === "services" && (
				<StaffServicesModal
					isOpen
					onClose={() => setActiveModal(null)}
					shopServices={shopServices}
					staffServices={staffServiceIds}
					onSave={(ids) => setStaffServiceIds(ids)}
				/>
			)}
			{activeModal === "avatar" && (
				<ProfileUploadModal
					isOpen
					staffProfile={{ userId: staff.userId, photo: staff.photo }}
					onClose={() => setActiveModal(null)}
					onUploaded={(url) => {
						setStaff((prev) => ({ ...prev, photo: url }));
						setShopData((prev) => ({
							...prev,
							staffs: prev.staffs.map((s) => (s.userId === staff.userId ? { ...s, photo: url } : s)),
						}));
					}}
				/>
			)}
		</div>
	);
};

/* ── inline carousel ── */
function StaffCarousel({ images, onShare, shareCopied }) {
	const trackRef = useRef(null);
	const [idx, setIdx] = useState(0);
	const isDragging = useRef(false);
	const startX = useRef(0);
	const deltaX = useRef(0);

	const hasImages = images && images.length > 0;
	const goTo = (n) => setIdx((n + images.length) % images.length);

	useEffect(() => {
		if (trackRef.current) {
			trackRef.current.style.transform = `translateX(${-idx * 100}%)`;
		}
	}, [idx]);

	const onTouchStart = (e) => {
		isDragging.current = true;
		startX.current = e.touches ? e.touches[0].clientX : e.clientX;
		deltaX.current = 0;
	};
	const onTouchMove = (e) => {
		if (!isDragging.current) return;
		deltaX.current = (e.touches ? e.touches[0].clientX : e.clientX) - startX.current;
		if (trackRef.current) {
			trackRef.current.style.transition = "none";
			trackRef.current.style.transform = `translateX(calc(${-idx * 100}% + ${deltaX.current}px))`;
		}
	};
	const onTouchEnd = () => {
		isDragging.current = false;
		if (Math.abs(deltaX.current) > 60) deltaX.current < 0 ? goTo(idx + 1) : goTo(idx - 1);
		if (trackRef.current) {
			trackRef.current.style.transition = "transform 300ms ease";
			trackRef.current.style.transform = `translateX(${-idx * 100}%)`;
		}
		deltaX.current = 0;
	};

	return (
		<div className="sf-profile-carousel" aria-roledescription="carousel">
			<div className="sf-profile-carousel-top-bar" style={{ pointerEvents: "auto" }}>
				<button className={`sf-carousel-overlay-btn ${shareCopied ? "sfc-share-copied" : ""}`} onClick={onShare} title="Share booking page">
					<Share2 size={18} />
				</button>
			</div>

			<div
				className="sf-profile-carousel-viewport"
				onTouchStart={onTouchStart}
				onTouchMove={onTouchMove}
				onTouchEnd={onTouchEnd}
				onMouseDown={(e) => {
					e.preventDefault();
					onTouchStart(e);
					window.addEventListener("mousemove", onTouchMove);
					window.addEventListener(
						"mouseup",
						() => {
							onTouchEnd();
							window.removeEventListener("mousemove", onTouchMove);
						},
						{ once: true },
					);
				}}
			>
				{hasImages ? (
					<div className="sf-profile-carousel-track" ref={trackRef}>
						{images.map((img, i) => (
							<div className="sf-profile-carousel-slide" key={i} aria-hidden={i !== idx}>
								<img className="sf-profile-carousel-img" src={img} alt={`Gallery ${i + 1}`} draggable={false} />
							</div>
						))}
					</div>
				) : (
					<div className="sf-profile-carousel-empty">
						<p>📷</p>
					</div>
				)}

				{hasImages && images.length > 1 && (
					<>
						<button className="sf-profile-carousel-arrow sf-profile-carousel-arrow--left" onClick={() => goTo(idx - 1)}>
							‹
						</button>
						<button className="sf-profile-carousel-arrow sf-profile-carousel-arrow--right" onClick={() => goTo(idx + 1)}>
							›
						</button>
					</>
				)}
			</div>

			{hasImages && images.length > 1 && (
				<div className="sf-profile-carousel-dots">
					{images.map((_, i) => (
						<button
							key={i}
							className={`sf-profile-carousel-dot ${i === idx ? "is-active" : ""}`}
							onClick={() => goTo(i)}
							aria-label={`Go to slide ${i + 1}`}
						/>
					))}
				</div>
			)}
		</div>
	);
}

export default ProfileStaffCard;
